import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  ChevronDown,
  FolderOpen,
  Folder,
  FileText,
  Copy,
  ArrowRight,
  Archive,
  GitMerge,
  CheckCircle2,
  Search,
  Check,
  X,
  Loader2,
  Lock,
  Eye,
  Hash,
} from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

import PageHeader from '../../components/layout/PageHeader';
import DocumentPreviewModal from '../../components/ui/DocumentPreviewModal';
import Table, { type TableColumn } from '../../components/ui/Table';
import { contentManagementService } from '../../services/contentManagement.service';
import masterService from '../../services/master.service';
import type {
  PendingApproval,
  PendingCaseHashtagApproval,
  DestinationFolder,
} from '../../types/contentManagement.types';
import type { RrmsDocument } from '../../types/document.types';
import { useDebounce } from '../../hooks/useDebounce';
import { canPreviewDocument } from '../../utils/documentAccessMode';
import {
  buildStoragePathTree,
  comparePathLabels,
  type StoragePathNode,
} from '../../utils/storagePathTree';
import { ROUTES } from '../../config/routes.config';

/** Document rows carrying their storage path (Section / … / Year / Date). */
type PathDoc = RrmsDocument & { path_segments: string[] };
type ContentPathNode = StoragePathNode<PathDoc>;

/** Case entries for the Copy/Move destination picker, on their date folder. */
interface PickerCase {
  case_id: string;
  label: string;
  path_segments: string[];
}
type PickerPathNode = StoragePathNode<PickerCase>;

interface PickerSelectedCase {
  case_id: string;
  label: string;
  path: string;
}

interface TreeItemProps {
  label: string;
  icon?: React.ReactNode;
  level: number;
  isActive?: boolean;
  hasChildren?: boolean;
  isExpanded?: boolean;
  count?: number;
  onClick: () => void;
  onToggle?: () => void;
}

const TreeItem: React.FC<TreeItemProps> = ({
  label,
  icon,
  level,
  isActive,
  hasChildren,
  isExpanded,
  count,
  onClick,
  onToggle,
}) => (
  <div
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    }}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--spacing-1)',
      padding: 'var(--spacing-2) var(--spacing-3)',
      paddingLeft: `${level * 16 + 12}px`,
      cursor: 'pointer',
      borderRadius: 'var(--radius-sm)',
      background: isActive ? 'var(--color-primary-50)' : 'transparent',
      color: isActive ? 'var(--color-primary)' : 'var(--color-gray-700)',
      fontSize: 'var(--font-size-sm)',
      transition: 'background var(--transition-fast)',
      userSelect: 'none',
    }}
    onMouseEnter={(e) => {
      if (!isActive) e.currentTarget.style.background = 'var(--color-gray-50)';
    }}
    onMouseLeave={(e) => {
      if (!isActive) e.currentTarget.style.background = 'transparent';
    }}
  >
    {hasChildren ? (
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          onToggle?.();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            onToggle?.();
          }
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          color: 'var(--color-gray-400)',
          flexShrink: 0,
        }}
      >
        {isExpanded ? (
          <ChevronDown size={12} />
        ) : (
          <ChevronRight size={12} />
        )}
      </span>
    ) : (
      <span style={{ width: 12, flexShrink: 0 }} />
    )}
    {icon && <span style={{ flexShrink: 0 }}>{icon}</span>}
    <span
      style={{
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
    {count !== undefined && count > 0 && (
      <span
        style={{
          fontSize: '10px',
          background: 'var(--color-gray-200)',
          color: 'var(--color-gray-600)',
          borderRadius: 'var(--radius-full)',
          padding: '1px 6px',
          flexShrink: 0,
        }}
      >
        {count}
      </span>
    )}
  </div>
);

const DocumentCard: React.FC<{
  doc: RrmsDocument;
  selected: boolean;
  onToggle: (id: string) => void;
}> = ({ doc, selected, onToggle }) => (
  <div
    role="button"
    tabIndex={0}
    onClick={() => onToggle(doc.document_id)}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onToggle(doc.document_id);
      }
    }}
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 'var(--spacing-3)',
      padding: 'var(--spacing-3)',
      borderRadius: 'var(--radius-md)',
      border: `1px solid ${
        selected ? 'var(--color-primary)' : 'var(--color-gray-200)'
      }`,
      background: selected ? 'var(--color-primary-50)' : 'white',
      cursor: 'pointer',
      transition: 'all var(--transition-fast)',
      marginBottom: 'var(--spacing-2)',
    }}
  >
    <div
      style={{
        width: 18,
        height: 18,
        borderRadius: 'var(--radius-sm)',
        flexShrink: 0,
        marginTop: 1,
        border: `2px solid ${
          selected ? 'var(--color-primary)' : 'var(--color-gray-300)'
        }`,
        background: selected ? 'var(--color-primary)' : 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all var(--transition-fast)',
      }}
    >
      {selected && <Check size={11} style={{ color: 'white' }} />}
    </div>
    <FileText
      size={16}
      style={{
        color: 'var(--color-gray-400)',
        flexShrink: 0,
        marginTop: 1,
      }}
    />
    <div style={{ flex: 1, minWidth: 0 }}>
      <p
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: 'var(--color-gray-800)',
        }}
      >
        {doc.original_file_name}
      </p>
      <div
        style={{
          display: 'flex',
          gap: 'var(--spacing-2)',
          marginTop: '4px',
          flexWrap: 'wrap',
        }}
      >
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            padding: '1px 6px',
            borderRadius: 'var(--radius-full)',
            background:
              doc.classification === 'Confidential'
                ? 'var(--color-gray-100)'
                : 'var(--color-info-light)',
            color:
              doc.classification === 'Confidential'
                ? 'var(--color-gray-600)'
                : 'var(--color-info)',
          }}
        >
          {doc.classification}
        </span>
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-gray-400)',
          }}
        >
          {format(new Date(doc.created_at), 'dd MMM yyyy')}
        </span>
      </div>
    </div>
  </div>
);

interface DestPickerProps {
  operationType: 'Copy' | 'Move' | 'Merge';
  sourceDocIds: string[];
  onClose: () => void;
  onSuccess: () => void;
}

const DestinationPickerModal: React.FC<DestPickerProps> = ({
  operationType,
  sourceDocIds,
  onClose,
  onSuccess,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);

  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(
    () => new Set()
  );

  const [selectedCase, setSelectedCase] = useState<PickerSelectedCase | null>(
    null
  );

  const [mergeSourceCase, setMergeSourceCase] =
    useState<PickerSelectedCase | null>(null);

  const [mergeDestCase, setMergeDestCase] =
    useState<PickerSelectedCase | null>(null);

  const [folderType, setFolderType] = useState('');
  const [documentType, setDocumentType] = useState('');

  const queryClient = useQueryClient();

  // Cases positioned on their storage path (Section / … / Year / Date), so
  // the picker browses the same layout the files are stored in.
  const { data: pickerCases = [], isLoading: treeLoading } = useQuery({
    queryKey: ['content-tree-destination'],
    queryFn: () =>
      contentManagementService.getContentTree({}),
    select: (r) => {
      const d = r.data?.data ?? r.data;
      return (d?.cases ?? []) as PickerCase[];
    },
  });

  const { data: destFolders } = useQuery({
    queryKey: ['dest-folders', selectedCase?.case_id],
    queryFn: () =>
      contentManagementService.getDestinationFolders(selectedCase!.case_id),
    enabled: !!selectedCase?.case_id && operationType !== 'Merge',
    select: (r) =>
      (r.data?.data?.existing_folders ?? r.data?.existing_folders ?? []) as (
        | DestinationFolder
        | { folder_type: string; document_type: string }
      )[],
  });

  const { data: masterDocTypes } = useQuery({
    queryKey: ['master-doc-types', folderType],
    queryFn: () => masterService.getDocumentTypes(folderType),
    enabled: !!folderType,
    select: (r) => r.data?.data ?? r.data ?? [],
  });

  const operationMutation = useMutation({
    mutationFn: () =>
      contentManagementService.performOperation({
        operation_type: operationType,
        source_document_ids: sourceDocIds,
        destination_case_id:
          operationType === 'Merge'
            ? mergeDestCase?.case_id
            : selectedCase?.case_id,
        destination_folder_type:
          operationType === 'Merge' ? undefined : folderType || undefined,
        destination_document_type:
          operationType === 'Merge' ? undefined : documentType || undefined,
      }),
    onSuccess: () => {
      toast.success(`${operationType} completed successfully`);
      queryClient.invalidateQueries({ queryKey: ['content-tree'] });
      queryClient.invalidateQueries({ queryKey: ['case-documents'] });
      // Invalidate audit cache so the audit log reflects this operation immediately
      queryClient.invalidateQueries({ queryKey: ['audit'] });
      onSuccess();
      onClose();
    },
    onError: (err: any) => {
      toast.error(
        err.response?.data?.message || `${operationType} failed`
      );
    },
  });

  useEffect(() => {
    // Reset all state when operation type changes
    setSearchQuery('');
    setExpandedNodes(new Set());
    setSelectedCase(null);
    setMergeSourceCase(null);
    setMergeDestCase(null);
    setFolderType('');
    setDocumentType('');
  }, [operationType]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    if (!selectedCase) {
      setFolderType('');
      setDocumentType('');
      return;
    }
    setFolderType('');
    setDocumentType('');
  }, [selectedCase?.case_id]);

  const filteredCases = React.useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return pickerCases;
    return pickerCases.filter(
      (c) =>
        c.label.toLowerCase().includes(q) ||
        (c.path_segments ?? []).some((s) => s.toLowerCase().includes(q))
    );
  }, [pickerCases, debouncedSearch]);

  const pickerTree = React.useMemo(
    () => buildStoragePathTree(filteredCases),
    [filteredCases]
  );

  // While searching, open every folder so the matches are visible.
  const forceExpand = debouncedSearch.trim().length > 0;

  useEffect(() => {
    // Auto-expand the top-level section nodes on first load
    if (!pickerCases.length || expandedNodes.size > 0) return;
    const next = new Set<string>();
    pickerCases.forEach((c) => {
      if (c.path_segments?.[0]) next.add(`path:${c.path_segments[0]}`);
    });
    setExpandedNodes(next);
  }, [pickerCases, expandedNodes.size]);

  const toggleNode = (nodeKey: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeKey)) next.delete(nodeKey);
      else next.add(nodeKey);
      return next;
    });
  };

  const foldersForType =
    destFolders?.filter((f) => f.folder_type === folderType) ?? [];

  const documentTypeOptions = React.useMemo(() => {
    const names = new Set<string>();
    (masterDocTypes ?? []).forEach((dt: any) => {
      if (dt?.name) names.add(dt.name);
    });
    foldersForType.forEach((f) => {
      if (f.document_type) names.add(f.document_type);
    });
    return Array.from(names);
  }, [masterDocTypes, foldersForType]);

  const canSubmit =
    operationType === 'Merge'
      ? !!mergeSourceCase && !!mergeDestCase
      : !!selectedCase && !!folderType && !!documentType.trim();

  // Recursive renderer over the storage layout — Section / Sub-Section /
  // File Category / Year / File Start Date — with the selectable case
  // entries (Copy/Move destinations) inside their date folder.
  const renderPickerLevel = (
    node: PickerPathNode,
    segments: string[],
    depth: number,
    selected: PickerSelectedCase | null,
    onSelect: (c: PickerSelectedCase) => void
  ): React.ReactNode[] =>
    Object.keys(node.children)
      .sort(comparePathLabels)
      .map((label) => {
        const child = node.children[label];
        const childPath = [...segments, label];
        const id = `path:${childPath.join('/')}`;
        const expanded = forceExpand || expandedNodes.has(id);
        return (
          <div key={id}>
            <div
              role="button"
              tabIndex={0}
              onClick={() => toggleNode(id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleNode(id);
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-2)',
                padding: '4px 10px',
                paddingLeft: 10 + depth * 14,
                cursor: 'pointer',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  color: 'var(--color-gray-500)',
                }}
              >
                {expanded ? (
                  <ChevronDown size={12} />
                ) : (
                  <ChevronRight size={12} />
                )}
              </span>
              <Folder
                size={13}
                style={{ color: 'var(--color-accent)', flexShrink: 0 }}
              />
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-gray-700)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </span>
            </div>
            {expanded &&
              renderPickerLevel(child, childPath, depth + 1, selected, onSelect)}
            {expanded &&
              child.items
                .slice()
                .sort((a, b) => a.label.localeCompare(b.label))
                .map((c) => {
                  const isSelected = selected?.case_id === c.case_id;
                  const displayLabel =
                    c.label.length > 16 ? `${c.label.slice(0, 15)}…` : c.label;
                  const pick = () =>
                    onSelect({
                      case_id: c.case_id,
                      label: c.label,
                      path: childPath.join(' / '),
                    });
                  return (
                    <div
                      key={c.case_id}
                      role="button"
                      tabIndex={0}
                      onClick={pick}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          pick();
                        }
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-2)',
                        padding: '4px 10px',
                        paddingLeft: 10 + (depth + 1) * 14,
                        cursor: 'pointer',
                        borderRadius: 'var(--radius-md)',
                        borderLeft: isSelected
                          ? '2px solid var(--color-primary)'
                          : '2px solid transparent',
                        background: isSelected
                          ? 'var(--color-primary-50)'
                          : 'transparent',
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.background =
                            'var(--color-gray-50)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.background = 'transparent';
                        }
                      }}
                      title={c.label}
                    >
                      <FileText
                        size={13}
                        style={{
                          color: 'var(--color-gray-400)',
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          fontFamily: 'var(--font-family-mono)',
                          fontSize: 'var(--font-size-xs)',
                          color: isSelected
                            ? 'var(--color-primary)'
                            : 'var(--color-gray-700)',
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {displayLabel}
                      </span>
                      {isSelected && (
                        <Check
                          size={12}
                          style={{
                            color: 'var(--color-primary)',
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </div>
                  );
                })}
          </div>
        );
      });

  const renderCaseTree = ({
    selected,
    onSelect,
  }: {
    selected: PickerSelectedCase | null;
    onSelect: (c: PickerSelectedCase) => void;
  }) => {
    if (treeLoading) {
      return (
        <div
          style={{
            padding: 'var(--spacing-4)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-2)',
          }}
        >
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              style={{
                height: 20,
                borderRadius: 4,
                background: 'var(--color-gray-100)',
              }}
            />
          ))}
        </div>
      );
    }

    if (filteredCases.length === 0) {
      return (
        <div
          style={{
            padding: 'var(--spacing-6)',
            textAlign: 'center',
            color: 'var(--color-gray-400)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          No cases found
        </div>
      );
    }

    return (
      <div
        style={{
          padding: 'var(--spacing-2) var(--spacing-1) var(--spacing-3)',
        }}
      >
        {renderPickerLevel(pickerTree, [], 0, selected, onSelect)}
      </div>
    );
  };

  return (
    <>
      <div
        role="presentation"
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 9998,
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 9999,
          background: 'white',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-xl)',
          width: '100%',
          maxWidth: 960,
          padding: 'var(--spacing-6)',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--spacing-5)',
          }}
        >
          <div>
            <h2
              style={{
                fontSize: 'var(--font-size-xl)',
                fontWeight: 'var(--font-weight-bold)',
                marginBottom: '4px',
              }}
            >
              {operationType === 'Merge'
                ? 'Merge Cases'
                : `${operationType} ${sourceDocIds.length} Document${
                    sourceDocIds.length !== 1 ? 's' : ''
                  }`}
            </h2>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-gray-500)',
              }}
            >
              {operationType === 'Merge'
                ? 'Select source and destination cases to merge'
                : 'Browse and select the destination case, folder type, and document type'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-gray-400)',
            }}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {operationType === 'Merge' && (
          <div
            style={{
              marginBottom: 'var(--spacing-4)',
              padding: 'var(--spacing-2) var(--spacing-3)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-warning-light)',
              color: 'var(--color-warning-dark, #92400e)',
              fontSize: 'var(--font-size-xs)',
            }}
          >
            <strong style={{ fontWeight: 'var(--font-weight-semibold)' }}>
              ⚠️ This action cannot be undone.
            </strong>{' '}
            All documents from the source case will be moved to the destination
            case.
          </div>
        )}

        <div
          style={{
            display: 'flex',
            gap: 'var(--spacing-4)',
            flex: 1,
            minHeight: 0,
          }}
        >
          <div
            style={{
              flex: operationType === 'Merge' ? 1 : 1.2,
              borderRight: '1px solid var(--color-gray-200)',
              paddingRight: 'var(--spacing-4)',
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
            }}
          >
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: 'var(--font-weight-semibold)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'var(--color-gray-500)',
                marginBottom: 'var(--spacing-2)',
              }}
            >
              {operationType === 'Merge' ? 'Select Source Case' : 'Browse'}
            </p>
            <div style={{ position: 'relative', marginBottom: 'var(--spacing-2)' }}>
              <Search
                size={14}
                style={{
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--color-gray-400)',
                }}
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search cases..."
                style={{
                  width: '100%',
                  padding:
                    'var(--spacing-2) var(--spacing-3) var(--spacing-2) 32px',
                  border: '1px solid var(--color-gray-200)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                }}
              />
            </div>
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-gray-100)',
                background: 'var(--color-gray-25, #fcfcfd)',
              }}
            >
              {renderCaseTree({
                selected:
                  operationType === 'Merge' ? mergeSourceCase : selectedCase,
                onSelect:
                  operationType === 'Merge'
                    ? (c) => setMergeSourceCase(c)
                    : (c) => setSelectedCase(c),
              })}
            </div>
          </div>

          <div
            style={{
              flex: 1,
              paddingLeft: 'var(--spacing-2)',
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
            }}
          >
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: 'var(--font-weight-semibold)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'var(--color-gray-500)',
                marginBottom: 'var(--spacing-2)',
              }}
            >
              {operationType === 'Merge'
                ? 'Select Destination Case'
                : 'Selected Destination'}
            </p>

            {operationType === 'Merge' ? (
              <>
                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-gray-100)',
                    background: 'var(--color-gray-25, #fcfcfd)',
                    marginBottom: 'var(--spacing-3)',
                  }}
                >
                  {renderCaseTree({
                    selected: mergeDestCase,
                    onSelect: (c) => setMergeDestCase(c),
                  })}
                </div>
                {!mergeSourceCase || !mergeDestCase ? (
                  <p
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--color-gray-400)',
                      textAlign: 'center',
                    }}
                  >
                    Select both a source and destination case to merge.
                  </p>
                ) : null}
              </>
            ) : selectedCase ? (
              <>
                <div
                  style={{
                    borderRadius: 'var(--radius-lg)',
                    border: '1px solid var(--color-gray-200)',
                    padding: 'var(--spacing-3)',
                    marginBottom: 'var(--spacing-4)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 'var(--spacing-3)',
                    background: 'white',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--spacing-2)',
                    }}
                  >
                    <FolderOpen
                      size={18}
                      style={{ color: 'var(--color-primary)' }}
                    />
                    <div>
                      <p
                        style={{
                          fontFamily: 'var(--font-family-mono)',
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--color-gray-800)',
                        }}
                      >
                        {selectedCase.label.length > 18
                          ? `${selectedCase.label.slice(0, 17)}…`
                          : selectedCase.label}
                      </p>
                      <p
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-gray-500)',
                        }}
                      >
                        {selectedCase.path}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedCase(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--color-gray-400)',
                    }}
                    aria-label="Clear selected destination"
                  >
                    <X size={16} />
                  </button>
                </div>

                <div style={{ marginBottom: 'var(--spacing-4)' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-weight-medium)',
                      marginBottom: 'var(--spacing-1)',
                    }}
                  >
                    Folder Type *
                  </label>
                  <select
                    value={folderType}
                    onChange={(e) => {
                      setFolderType(e.target.value);
                      setDocumentType('');
                    }}
                    style={{
                      width: '100%',
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      border: '1px solid var(--color-gray-200)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--font-size-sm)',
                      background: 'white',
                    }}
                  >
                    <option value="">Select folder type</option>
                    <option value="Case Files">Case Files</option>
                    <option value="Correspondence">Correspondence</option>
                  </select>
                </div>

                <div style={{ marginBottom: 'var(--spacing-3)' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-weight-medium)',
                      marginBottom: 'var(--spacing-1)',
                    }}
                  >
                    Document Type *
                  </label>

                  {!folderType ? (
                    <select
                      disabled
                      value=""
                      style={{
                        width: '100%',
                        padding: 'var(--spacing-2) var(--spacing-3)',
                        border: '1px solid var(--color-gray-100)',
                        borderRadius: 'var(--radius-md)',
                        fontSize: 'var(--font-size-sm)',
                        background: 'var(--color-gray-50)',
                        color: 'var(--color-gray-400)',
                      }}
                    >
                      <option value="">Select folder type first</option>
                    </select>
                  ) : documentTypeOptions.length > 0 ? (
                    <select
                      value={
                        documentTypeOptions.includes(documentType) ? documentType : ''
                      }
                      onChange={(e) => setDocumentType(e.target.value)}
                      style={{
                        width: '100%',
                        padding: 'var(--spacing-2) var(--spacing-3)',
                        border: '1px solid var(--color-gray-200)',
                        borderRadius: 'var(--radius-md)',
                        fontSize: 'var(--font-size-sm)',
                        background: 'white',
                      }}
                    >
                      <option value="">Select document type</option>
                      {documentTypeOptions.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={documentType}
                      onChange={(e) => setDocumentType(e.target.value)}
                      placeholder="Enter document type..."
                      style={{
                        width: '100%',
                        padding: 'var(--spacing-2) var(--spacing-3)',
                        border: '1px solid var(--color-gray-200)',
                        borderRadius: 'var(--radius-md)',
                        fontSize: 'var(--font-size-sm)',
                      }}
                    />
                  )}
                </div>
              </>
            ) : (
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  borderRadius: 'var(--radius-lg)',
                  border: '1px dashed var(--color-gray-200)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 'var(--spacing-6)',
                  textAlign: 'center',
                  color: 'var(--color-gray-400)',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                Select a case from the tree to set as destination.
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            gap: 'var(--spacing-3)',
            justifyContent: 'flex-end',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: 'var(--spacing-2) var(--spacing-5)',
              border: '1px solid var(--color-gray-200)',
              borderRadius: 'var(--radius-md)',
              background: 'white',
              color: 'var(--color-gray-700)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => operationMutation.mutate()}
            disabled={!canSubmit || operationMutation.isPending}
            style={{
              padding: 'var(--spacing-2) var(--spacing-5)',
              background: 'var(--color-primary)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontWeight: 'var(--font-weight-semibold)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
              opacity: !canSubmit ? 0.5 : 1,
            }}
          >
            {operationMutation.isPending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : operationType === 'Copy' ? (
              <Copy size={14} />
            ) : operationType === 'Merge' ? (
              <GitMerge size={14} />
            ) : (
              <ArrowRight size={14} />
            )}
            {operationMutation.isPending
              ? 'Processing...'
              : `Confirm ${operationType}`}
          </button>
        </div>
      </div>
    </>
  );
};

function formatYearForDisplay(year: number): string {
  if (!Number.isFinite(year)) return '';
  if (year >= 0 && year < 100) return String(2000 + year);
  return String(year);
}

const ContentManagementPage: React.FC = () => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [yearSearch, setYearSearch] = useState('');
  const yearDropdownRef = useRef<HTMLDivElement | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  // Selected date folder in the storage-layout tree (full segment path).
  const [activePath, setActivePath] = useState<string[] | null>(null);
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);
  const [operationModal, setOperationModal] = useState<'Copy' | 'Move' | 'Merge' | null>(
    null
  );
  const [archiveConfirm, setArchiveConfirm] = useState(false);
  const [previewAccessDocId, setPreviewAccessDocId] = useState<
    string | number | null
  >(null);
  const [viewDetailsApprovalItem, setViewDetailsApprovalItem] = useState<
    PendingApproval | null
  >(null);
  const navigate = useNavigate();
  // Deny flows
  const [denyingDocId, setDenyingDocId] = useState<string | null>(null);
  const [docDenyReasons, setDocDenyReasons] = useState<Record<string, string>>(
    {}
  );
  const [denyingRequestId, setDenyingRequestId] = useState<string | null>(null);
  const [denyReasons, setDenyReasons] = useState<Record<string, string>>({});
  const [denyingCaseHashtagApprovalId, setDenyingCaseHashtagApprovalId] =
    useState<string | null>(null);
  const [caseHashtagDenyReasons, setCaseHashtagDenyReasons] = useState<
    Record<string, string>
  >({});
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'content' | 'approvals'>('content');
  const [approvalSubTab, setApprovalSubTab] = useState<'pending' | 'access' | 'case-hashtags'>('pending');
  const [approvalPage, setApprovalPage] = useState(1);
  const [accessPage, setAccessPage] = useState(1);
  const [caseHashtagPage, setCaseHashtagPage] = useState(1);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    document.title = 'RRMS | Content Management';
    return () => {
      document.title = 'RRMS';
    };
  }, []);

  useEffect(() => {
    const tab = searchParams.get('tab');
    const subTab = searchParams.get('subtab');
    if (tab === 'access-requests') {
      setActiveTab('approvals');
      setApprovalSubTab('access');
      return;
    }
    if (tab === 'pending-approvals') {
      setActiveTab('approvals');
      setApprovalSubTab('pending');
      return;
    }
    if (tab === 'approvals') {
      setActiveTab('approvals');
      if (subTab === 'access') {
        setApprovalSubTab('access');
      } else if (subTab === 'case-hashtags') {
        setApprovalSubTab('case-hashtags');
      } else {
        setApprovalSubTab('pending');
      }
      return;
    }
    if (tab === 'content' || tab === 'content-management') {
      setActiveTab('content');
    }
  }, [searchParams]);

  const { data: availableYears = [] } = useQuery({
    queryKey: ['content-tree-years'],
    queryFn: () => contentManagementService.getContentTree({}),
    select: (r) => {
      const d = r.data?.data ?? r.data;
      const t = d?.tree ?? d ?? {};
      return Object.keys(t)
        .map((y) => parseInt(y, 10))
        .filter((y) => Number.isFinite(y))
        .sort((a, b) => b - a);
    },
  });

  useEffect(() => {
    if (availableYears.length === 0) return;
    if (!availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
      setActivePath(null);
      setSelectedDocIds([]);
    }
  }, [availableYears, selectedYear]);

  useEffect(() => {
    const handleOutside = (event: MouseEvent) => {
      if (!yearDropdownRef.current) return;
      if (!yearDropdownRef.current.contains(event.target as Node)) {
        setYearDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const yearOptions = availableYears.map((y) => ({
    id: String(y),
    name: formatYearForDisplay(y),
  }));
  const filteredYearOptions = yearSearch.trim()
    ? yearOptions.filter((y) =>
        y.name.toLowerCase().includes(yearSearch.trim().toLowerCase())
      )
    : yearOptions;

  // Flat documents with path_segments mirroring the physical storage layout:
  // Section / Sub-Section / File Category / Year / File Start Date.
  const { data: pathDocs = [], isLoading: treeLoading } = useQuery({
    queryKey: ['content-tree', selectedYear],
    queryFn: () =>
      contentManagementService.getContentTree({ year: selectedYear }),
    select: (r) => {
      const d = r.data?.data ?? r.data;
      return (d?.documents ?? []) as PathDoc[];
    },
  });

  const pathTree = React.useMemo(() => buildStoragePathTree(pathDocs), [pathDocs]);

  // Documents of the selected date folder, straight from the tree payload —
  // refreshed automatically when ['content-tree'] is invalidated after ops.
  const nodeDocuments = React.useMemo(() => {
    if (!activePath) return [] as RrmsDocument[];
    let node: ContentPathNode | undefined = pathTree;
    for (const seg of activePath) {
      node = node.children[seg];
      if (!node) return [] as RrmsDocument[];
    }
    return node.items;
  }, [pathTree, activePath]);

  const approvalQuery = useQuery({
    queryKey: ['pending-approvals', approvalPage],
    queryFn: () =>
      contentManagementService.getPendingApprovals({ page: approvalPage, limit: 10 }),
    select: (r) => {
      const d = r.data?.data ?? r.data;
      return {
        items: (d?.items ?? []) as PendingApproval[],
        pagination: d?.pagination ?? { page: approvalPage, limit: 10, total: (d?.items ?? []).length, totalPages: 1, hasNextPage: false, hasPrevPage: false },
      };
    },
  });
  const pendingApprovals = approvalQuery.data?.items;
  const approvalPagination = approvalQuery.data?.pagination ?? { page: approvalPage, limit: 10, total: 0, totalPages: 1, hasNextPage: false, hasPrevPage: false };
  const refetchApprovals = approvalQuery.refetch;

  const accessQuery = useQuery({
    queryKey: ['pending-access-requests', accessPage],
    queryFn: () =>
      contentManagementService.getPendingAccessRequests({ page: accessPage, limit: 10 }),
    select: (r) => {
      const d = r.data?.data ?? r.data;
      return {
        items: d?.items ?? [],
        pagination: d?.pagination ?? { page: accessPage, limit: 10, total: (d?.items ?? []).length, totalPages: 1, hasNextPage: false, hasPrevPage: false },
      };
    },
  });
  const pendingAccessReqs = accessQuery.data?.items;
  const accessPagination = accessQuery.data?.pagination ?? { page: accessPage, limit: 10, total: 0, totalPages: 1, hasNextPage: false, hasPrevPage: false };
  const caseHashtagApprovalsQuery = useQuery({
    queryKey: ['pending-case-hashtag-approvals', caseHashtagPage],
    queryFn: () =>
      contentManagementService.getPendingCaseHashtagApprovals({
        page: caseHashtagPage,
        limit: 10,
      }),
    select: (r) => {
      const d = r.data?.data ?? r.data;
      return {
        items: (d?.items ?? []) as PendingCaseHashtagApproval[],
        pagination: d?.pagination ?? {
          page: caseHashtagPage,
          limit: 10,
          total: (d?.items ?? []).length,
          totalPages: 1,
          hasNextPage: false,
          hasPrevPage: false,
        },
      };
    },
  });
  const pendingCaseHashtagApprovals = caseHashtagApprovalsQuery.data?.items;
  const caseHashtagPagination =
    caseHashtagApprovalsQuery.data?.pagination ?? {
      page: caseHashtagPage,
      limit: 10,
      total: 0,
      totalPages: 1,
      hasNextPage: false,
      hasPrevPage: false,
    };
  const refetchCaseHashtagApprovals = caseHashtagApprovalsQuery.refetch;

  const caseHashtagApprovalColumns: TableColumn<PendingCaseHashtagApproval>[] = [
    {
      key: 'case_number',
      header: 'File No',
      minWidth: 170,
      tooltip: (item) => (item as any).file_number || item.cms_number,
      cellStyle: { fontWeight: 600, fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-sm)' },
      render: (item) => (
        <Link
          to={ROUTES.CASE_DETAIL.replace(':caseId', String(item.case_id))}
          style={{ color: 'var(--color-primary)', textDecoration: 'underline', cursor: 'pointer' }}
          title="Open case details"
        >
          {(item as any).file_number || item.cms_number}
        </Link>
      ),
    },
    {
      key: 'hashtags',
      header: 'Proposed Hashtags',
      minWidth: 260,
      clampLines: 2,
      tooltip: (item) =>
        Array.isArray(item.proposed_hashtags) && item.proposed_hashtags.length
          ? item.proposed_hashtags.map((t) => `#${t}`).join(', ')
          : '—',
      render: (item) =>
        Array.isArray(item.proposed_hashtags) && item.proposed_hashtags.length
          ? item.proposed_hashtags.map((t) => `#${t}`).join(', ')
          : '—',
    },
    {
      key: 'requested_by',
      header: 'Requested By',
      minWidth: 170,
      clampLines: 2,
      tooltip: (item) => item.requested_by_name,
      render: (item) => item.requested_by_name,
    },
    {
      key: 'date',
      header: 'Date',
      minWidth: 120,
      tooltip: (item) => format(new Date(item.created_at), 'dd MMM yyyy'),
      render: (item) => format(new Date(item.created_at), 'dd MMM yyyy'),
    },
    {
      key: 'actions',
      header: 'Actions',
      minWidth: 280,
      tooltip: () => 'Hashtag approval actions',
      render: (item) =>
        denyingCaseHashtagApprovalId === item.approval_id ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
            <textarea
              value={caseHashtagDenyReasons[item.approval_id] || ''}
              onChange={(e) =>
                setCaseHashtagDenyReasons((prev) => ({
                  ...prev,
                  [item.approval_id]: e.target.value,
                }))
              }
              placeholder="Denial reason (required)..."
              rows={2}
              style={{ width: '100%', minWidth: 180, padding: 'var(--spacing-2)', border: '1px solid var(--color-error)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-xs)', resize: 'none', fontFamily: 'inherit' }}
            />
            <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
              <button
                type="button"
                onClick={() => {
                  const reason = caseHashtagDenyReasons[item.approval_id]?.trim() || '';
                  if (!reason) {
                    toast.error('Please provide a denial reason');
                    return;
                  }
                  decideCaseHashtagMutation.mutate({
                    approvalId: item.approval_id,
                    action: 'Denied',
                    reason,
                  });
                  setDenyingCaseHashtagApprovalId(null);
                }}
                disabled={decideCaseHashtagMutation.isPending || !caseHashtagDenyReasons[item.approval_id]?.trim()}
                style={{ padding: '4px 10px', border: 'none', background: 'var(--color-error)', color: 'white', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: 'var(--font-size-xs)', fontWeight: 600, opacity: caseHashtagDenyReasons[item.approval_id]?.trim() ? 1 : 0.5 }}
              >
                Confirm Deny
              </button>
              <button
                type="button"
                onClick={() => {
                  setDenyingCaseHashtagApprovalId(null);
                  setCaseHashtagDenyReasons((prev) => {
                    const next = { ...prev };
                    delete next[item.approval_id];
                    return next;
                  });
                }}
                style={{ padding: '4px 10px', border: '1px solid var(--color-gray-200)', background: 'white', color: 'var(--color-gray-600)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: 'var(--font-size-xs)' }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() =>
                decideCaseHashtagMutation.mutate({
                  approvalId: item.approval_id,
                  action: 'Approved',
                  reason: '',
                })
              }
              disabled={decideCaseHashtagMutation.isPending}
              style={{ padding: '4px 12px', border: 'none', background: 'var(--color-success-light)', color: 'var(--color-success)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: 'var(--font-size-xs)', fontWeight: 600 }}
            >
              Approve
            </button>
            <button
              type="button"
              onClick={() => setDenyingCaseHashtagApprovalId(item.approval_id)}
              disabled={decideCaseHashtagMutation.isPending}
              style={{ padding: '4px 12px', border: 'none', background: 'var(--color-error-light)', color: 'var(--color-error)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: 'var(--font-size-xs)', fontWeight: 600 }}
            >
              Deny
            </button>
          </div>
        ),
    },
  ];
  const pendingApprovalColumns: TableColumn<PendingApproval>[] = [
    {
      key: 'document',
      header: 'Document',
      minWidth: 220,
      clampLines: 2,
      tooltip: (item) => item.original_file_name,
      render: (item) => item.original_file_name,
    },
    {
      key: 'case_number',
      header: 'File No',
      minWidth: 150,
      tooltip: (item) => (item as any).file_number || item.cms_number,
      cellStyle: { fontWeight: 600, fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-sm)' },
      render: (item) => (
        <Link
          to={ROUTES.CASE_DETAIL.replace(':caseId', String(item.case_id))}
          style={{ color: 'var(--color-primary)', textDecoration: 'underline', cursor: 'pointer' }}
          title="Open case details"
        >
          {(item as any).file_number || item.cms_number}
        </Link>
      ),
    },
    {
      key: 'hierarchy_path',
      header: 'Section',
      minWidth: 170,
      clampLines: 2,
      tooltip: (item) => (item as any).hierarchy_path || '—',
      render: (item) => (item as any).hierarchy_path || '—',
    },
    {
      key: 'category',
      header: 'Category / Subject',
      minWidth: 180,
      clampLines: 2,
      tooltip: (item) =>
        [(item as any).category, (item as any).subject].filter(Boolean).join(' / ') || '—',
      render: (item) =>
        [(item as any).category, (item as any).subject].filter(Boolean).join(' / ') || '—',
    },
    {
      key: 'classification',
      header: 'Classification',
      minWidth: 130,
      tooltip: (item) => item.classification,
      render: (item) => (
        <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 'var(--radius-full)', fontSize: 'var(--font-size-xs)', fontWeight: 600, background: item.classification === 'Confidential' ? 'var(--color-gray-100)' : 'var(--color-info-light)', color: item.classification === 'Confidential' ? 'var(--color-gray-600)' : 'var(--color-info)' }}>
          {item.classification}
        </span>
      ),
    },
    {
      key: 'uploaded_by',
      header: 'Uploaded By',
      minWidth: 150,
      clampLines: 2,
      tooltip: (item) => item.uploaded_by_name,
      render: (item) => item.uploaded_by_name,
    },
    {
      key: 'date',
      header: 'Date',
      minWidth: 120,
      tooltip: (item) => format(new Date(item.created_at), 'dd MMM yyyy'),
      render: (item) => format(new Date(item.created_at), 'dd MMM yyyy'),
    },
    {
      key: 'actions',
      header: 'Actions',
      minWidth: 250,
      tooltip: () => 'Approval actions',
      render: (item) =>
        denyingDocId === item.document_id ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
            <textarea
              value={docDenyReasons[item.document_id] || ''}
              onChange={(e) =>
                setDocDenyReasons((prev) => ({
                  ...prev,
                  [item.document_id]: e.target.value,
                }))
              }
              placeholder="Denial reason (required)..."
              rows={2}
              style={{ width: '100%', minWidth: 220, padding: 'var(--spacing-2)', border: '1px solid var(--color-error)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-xs)', resize: 'none', fontFamily: 'inherit' }}
            />
            <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
              <button
                type="button"
                onClick={() => {
                  const reason = docDenyReasons[item.document_id]?.trim() || '';
                  if (!reason) {
                    toast.error('Please provide a denial reason');
                    return;
                  }
                  decideMutation.mutate({ docId: item.document_id, action: 'Denied', reason });
                  setDenyingDocId(null);
                }}
                disabled={decideMutation.isPending || !docDenyReasons[item.document_id]?.trim()}
                style={{ padding: '4px 10px', border: 'none', background: 'var(--color-error)', color: 'white', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: 'var(--font-size-xs)', fontWeight: 600, opacity: docDenyReasons[item.document_id]?.trim() ? 1 : 0.5 }}
              >
                Confirm Deny
              </button>
              <button
                type="button"
                onClick={() => {
                  setDenyingDocId(null);
                  setDocDenyReasons((prev) => {
                    const next = { ...prev };
                    delete next[item.document_id];
                    return next;
                  });
                }}
                style={{ padding: '4px 10px', border: '1px solid var(--color-gray-200)', background: 'white', color: 'var(--color-gray-600)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: 'var(--font-size-xs)' }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" onClick={() => decideMutation.mutate({ docId: item.document_id, action: 'Approved', reason: '' })} disabled={decideMutation.isPending} style={{ padding: '4px 12px', border: 'none', background: 'var(--color-success-light)', color: 'var(--color-success)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>Approve</button>
            <button type="button" onClick={() => setDenyingDocId(item.document_id)} disabled={decideMutation.isPending} style={{ padding: '4px 12px', border: 'none', background: 'var(--color-error-light)', color: 'var(--color-error)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>Deny</button>
          </div>
        ),
    },
  ];
  const accessRequestColumns: TableColumn<any>[] = [
    {
      key: 'document',
      header: 'Document',
      minWidth: 220,
      clampLines: 2,
      tooltip: (req) => req.document?.original_file_name ?? req.original_file_name ?? 'Document',
      render: (req) => req.document?.original_file_name ?? req.original_file_name ?? 'Document',
    },
    {
      key: 'requester',
      header: 'Requester',
      minWidth: 180,
      clampLines: 2,
      tooltip: (req) => req.requested_by_name ?? req.requester_name ?? `User ${req.requested_by_user_id ?? ''}`,
      render: (req) => req.requested_by_name ?? req.requester_name ?? `User ${req.requested_by_user_id ?? ''}`,
    },
    {
      key: 'reason',
      header: 'Reason',
      minWidth: 240,
      clampLines: 2,
      tooltip: (req) => req.reason || '—',
      cellStyle: { color: 'var(--color-gray-500)' },
      render: (req) => req.reason || '—',
    },
    {
      key: 'date',
      header: 'Date',
      minWidth: 120,
      tooltip: (req) => req.created_at ? format(new Date(req.created_at), 'dd MMM yyyy') : '—',
      cellStyle: { color: 'var(--color-gray-500)' },
      render: (req) => req.created_at ? format(new Date(req.created_at), 'dd MMM yyyy') : '—',
    },
    {
      key: 'actions',
      header: 'Actions',
      minWidth: 260,
      tooltip: () => 'Access request actions',
      render: (req) =>
        denyingRequestId === req.request_id ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
            <textarea value={denyReasons[req.request_id] || ''} onChange={(e) => setDenyReasons((prev) => ({ ...prev, [req.request_id]: e.target.value }))} placeholder="Denial reason (required)..." rows={2} style={{ width: '100%', minWidth: 180, padding: 'var(--spacing-2)', border: '1px solid var(--color-error)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-xs)', resize: 'none', fontFamily: 'inherit' }} />
            <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
              <button type="button" onClick={() => { const reason = denyReasons[req.request_id]?.trim() || ''; if (!reason) { toast.error('Please provide a denial reason'); return; } decideAccessMutation.mutate({ reqId: req.request_id, decision: 'Denied', reason }); }} disabled={decideAccessMutation.isPending || !denyReasons[req.request_id]?.trim()} style={{ padding: '4px 10px', border: 'none', background: 'var(--color-error)', color: 'white', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: 'var(--font-size-xs)', fontWeight: 600, opacity: denyReasons[req.request_id]?.trim() ? 1 : 0.5 }}>Confirm Deny</button>
              <button type="button" onClick={() => { setDenyingRequestId(null); setDenyReasons((prev) => { const next = { ...prev }; delete next[req.request_id]; return next; }); }} style={{ padding: '4px 10px', border: '1px solid var(--color-gray-200)', background: 'white', color: 'var(--color-gray-600)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: 'var(--font-size-xs)' }}>Cancel</button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            {req.document?.document_id &&
              canPreviewDocument({
                fileName: req.document?.original_file_name ?? req.original_file_name,
              }) && (
              <button type="button" onClick={() => setPreviewAccessDocId(req.document.document_id)} title="Preview" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--color-primary)' }}><Eye size={15} /></button>
            )}
            <button type="button" onClick={() => decideAccessMutation.mutate({ reqId: req.request_id, decision: 'Approved', reason: '' })} disabled={decideAccessMutation.isPending} style={{ padding: '4px 12px', border: 'none', background: 'var(--color-success-light)', color: 'var(--color-success)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>Approve</button>
            <button type="button" onClick={() => setDenyingRequestId(req.request_id)} disabled={decideAccessMutation.isPending} style={{ padding: '4px 12px', border: 'none', background: 'var(--color-error-light)', color: 'var(--color-error)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>Deny</button>
          </div>
        ),
    },
  ];
  const refetchAccessReqs = accessQuery.refetch;

  const decideMutation = useMutation({
    mutationFn: ({
      docId,
      action,
      reason,
    }: {
      docId: string;
      action: 'Approved' | 'Denied';
      reason: string;
    }) =>
      contentManagementService.decideDocument(docId, {
        action,
        reason,
      }),
    onSuccess: (_, { action }) => {
      toast.success(`Document ${action.toLowerCase()} successfully`);
      refetchApprovals();
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Decision failed');
    },
  });

  const decideAccessMutation = useMutation({
    mutationFn: ({
      reqId,
      decision,
      reason,
    }: {
      reqId: string;
      decision: 'Approved' | 'Denied';
      reason: string;
    }) =>
      contentManagementService.decideAccessRequest(reqId, {
        decision,
        decision_reason: reason,
      }),
    onSuccess: (_, { decision }) => {
      toast.success(`Access request ${decision.toLowerCase()}`);
      refetchAccessReqs();
    },
    onError: (err: any) => {
      toast.error(
        err.response?.data?.message || 'Failed to decide access request'
      );
    },
  });

  const decideCaseHashtagMutation = useMutation({
    mutationFn: ({
      approvalId,
      action,
      reason,
    }: {
      approvalId: string;
      action: 'Approved' | 'Denied';
      reason: string;
    }) =>
      contentManagementService.decideCaseHashtagApproval(approvalId, {
        action,
        reason,
      }),
    onSuccess: (_, { action }) => {
      toast.success(`Case hashtags ${action.toLowerCase()} successfully`);
      refetchCaseHashtagApprovals();
      queryClient.invalidateQueries({ queryKey: ['case'] });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to decide case hashtags');
    },
  });

  const archiveMutation = useMutation({
    mutationFn: () =>
      contentManagementService.performOperation({
        operation_type: 'Archive',
        source_document_ids: selectedDocIds,
      }),
    onSuccess: () => {
      toast.success(
        `${selectedDocIds.length} document${
          selectedDocIds.length !== 1 ? 's' : ''
        } archived`
      );
      setSelectedDocIds([]);
      setArchiveConfirm(false);
      queryClient.invalidateQueries({ queryKey: ['case-documents'] });
      queryClient.invalidateQueries({ queryKey: ['content-tree'] });
      // Invalidate audit cache so the audit log reflects this archive operation immediately
      queryClient.invalidateQueries({ queryKey: ['audit'] });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Archive failed');
      setArchiveConfirm(false);
    },
  });

  const toggleNode = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  };

  const toggleDocSelection = (docId: string) => {
    setSelectedDocIds((prev) =>
      prev.includes(docId) ? prev.filter((id) => id !== docId) : [...prev, docId]
    );
  };

  const selectAll = () => {
    if (!nodeDocuments.length) return;
    setSelectedDocIds((prev) =>
      prev.length === nodeDocuments.length
        ? []
        : nodeDocuments.map((d) => d.document_id)
    );
  };

  // Recursive renderer over the storage layout: Section / Sub-Section /
  // File Category / Year / File Start Date. Date folders (leaves, where the
  // documents sit) open the file panel; every other level expands/collapses.
  const renderPathLevel = (
    node: ContentPathNode,
    segments: string[],
    level: number
  ): React.ReactNode[] =>
    Object.keys(node.children)
      .sort(comparePathLabels)
      .map((label) => {
        const child = node.children[label];
        const childPath = [...segments, label];
        const id = `path:${childPath.join('/')}`;
        const hasChildren = Object.keys(child.children).length > 0;
        const clickable = child.items.length > 0 || !hasChildren;
        const expanded = expandedNodes.has(id);
        const isActive =
          !!activePath && activePath.join('/') === childPath.join('/');
        return (
          <div key={id}>
            <TreeItem
              label={label}
              level={level}
              hasChildren={hasChildren}
              isExpanded={expanded}
              isActive={isActive}
              count={child.items.length > 0 ? child.items.length : undefined}
              icon={
                hasChildren ? (
                  expanded ? (
                    <FolderOpen size={13} />
                  ) : (
                    <Folder size={13} />
                  )
                ) : (
                  <Folder
                    size={13}
                    style={{ color: 'var(--color-primary-light)' }}
                  />
                )
              }
              onClick={() => {
                if (clickable) {
                  setActivePath(childPath);
                  setSelectedDocIds([]);
                } else {
                  toggleNode(id);
                }
              }}
              onToggle={hasChildren ? () => toggleNode(id) : undefined}
            />
            {hasChildren && expanded && renderPathLevel(child, childPath, level + 1)}
          </div>
        );
      });

  const renderTree = () => {
    if (treeLoading) {
      return [...Array(4)].map((_, i) => (
        <div
          key={i}
          style={{
            height: 28,
            margin: 'var(--spacing-1) var(--spacing-2)',
            background: 'var(--color-gray-100)',
            borderRadius: 4,
          }}
        />
      ));
    }
    if (pathDocs.length === 0) {
      return (
        <div
          style={{
            padding: 'var(--spacing-4)',
            textAlign: 'center',
            color: 'var(--color-gray-400)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          No content for {formatYearForDisplay(selectedYear)}
        </div>
      );
    }
    return renderPathLevel(pathTree, [], 0);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-3)', flexWrap: 'wrap', gap: 'var(--spacing-3)' }}>
        <PageHeader title="Content Management" subtitle="Manage, organise, and review case documents" />
        {activeTab === 'content' && (
          <div ref={yearDropdownRef} style={{ minWidth: 180, position: 'relative' }}>
            <button
              type="button"
              onClick={() => {
                setYearDropdownOpen((prev) => !prev);
                setYearSearch('');
              }}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: `1px solid ${yearDropdownOpen ? 'var(--color-primary)' : 'var(--color-gray-200)'}`,
                borderRadius: '0.375rem',
                fontSize: '1.1rem',
                minHeight: '3.2rem',
                boxSizing: 'border-box',
                background: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
              }}
            >
              <span>{formatYearForDisplay(selectedYear)}</span>
              <ChevronDown size={16} />
            </button>
            {yearDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  right: 0,
                  width: '220px',
                  maxWidth: 'calc(100vw - 32px)',
                  border: '1px solid var(--color-gray-200)',
                  borderRadius: '0.5rem',
                  background: 'white',
                  boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)',
                  zIndex: 1200,
                  overflow: 'hidden',
                }}
              >
                <div style={{ padding: '0.5rem' }}>
                  <input
                    type="text"
                    value={yearSearch}
                    onChange={(e) => setYearSearch(e.target.value)}
                    placeholder={treeLoading ? 'Loading...' : 'Search...'}
                    style={{
                      width: '100%',
                      padding: '0.45rem 0.6rem',
                      border: '1px solid var(--color-gray-200)',
                      borderRadius: '0.375rem',
                      fontSize: '0.9rem',
                      boxSizing: 'border-box',
                      outline: 'none',
                    }}
                  />
                </div>
                <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                  {filteredYearOptions.map((opt) => {
                    const isActive = String(selectedYear) === String(opt.id);
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => {
                          const parsed = parseInt(String(opt.id), 10);
                          if (Number.isFinite(parsed)) {
                            setSelectedYear(parsed);
                            setActivePath(null);
                            setSelectedDocIds([]);
                          }
                          setYearDropdownOpen(false);
                        }}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          border: 'none',
                          background: isActive
                            ? 'var(--color-primary-50)'
                            : 'white',
                          padding: '0.55rem 0.75rem',
                          fontSize: '1rem',
                          cursor: 'pointer',
                        }}
                      >
                        {opt.name}
                      </button>
                    );
                  })}
                  {filteredYearOptions.length === 0 && (
                    <div
                      style={{
                        padding: '0.6rem 0.75rem',
                        color: 'var(--color-gray-500)',
                        fontSize: '0.9rem',
                      }}
                    >
                      No years found
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--color-gray-200)', marginBottom: 'var(--spacing-4)' }}>
        {(['content', 'approvals'] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            style={{
              padding: 'var(--spacing-2) var(--spacing-5)',
              border: 'none',
              background: 'transparent',
              fontSize: 'var(--font-size-sm)',
              fontWeight: activeTab === tab ? 600 : 500,
              color: activeTab === tab ? 'var(--color-primary)' : 'var(--color-gray-500)',
              cursor: 'pointer',
              borderBottom: activeTab === tab ? '2px solid var(--color-primary)' : '2px solid transparent',
              marginBottom: '-2px',
              transition: 'color 0.15s, border-color 0.15s',
            }}
          >
            {tab === 'content' ? 'Content Management' : 'Approvals'}
          </button>
        ))}
      </div>

      {activeTab === 'content' && (
        <div style={{ display: 'flex', gap: 'var(--spacing-4)', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <div className="card" style={{ width: 280, flexShrink: 0, overflow: 'auto', padding: 'var(--spacing-2)' }}>
            <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: 'var(--spacing-2) var(--spacing-3)', marginBottom: 'var(--spacing-1)' }}>
              Document Tree
            </p>
            {renderTree()}
          </div>
          <div className="card" style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
            {!activePath ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 'var(--spacing-3)', color: 'var(--color-gray-400)' }}>
                <FolderOpen size={48} style={{ color: 'var(--color-gray-200)' }} />
                <p style={{ fontSize: 'var(--font-size-sm)' }}>Select a date folder from the tree to view files</p>
              </div>
            ) : (
              <>
                <div style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-gray-100)', display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-700)' }}>
                      {activePath.join(' / ')}
                    </p>
                    <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-400)' }}>
                      {nodeDocuments.length} document{nodeDocuments.length !== 1 ? 's' : ''}
                      {selectedDocIds.length > 0 && ` · ${selectedDocIds.length} selected`}
                    </p>
                  </div>
                  {nodeDocuments.length > 0 && (
                    <button type="button" onClick={selectAll} style={{ fontSize: 'var(--font-size-xs)', padding: 'var(--spacing-1) var(--spacing-3)', border: '1px solid var(--color-gray-200)', borderRadius: 'var(--radius-md)', background: 'white', cursor: 'pointer', color: 'var(--color-gray-600)' }}>
                      {selectedDocIds.length === nodeDocuments.length ? 'Deselect All' : 'Select All'}
                    </button>
                  )}
                  {selectedDocIds.length > 0 && (
                    <>
                      <button type="button" onClick={() => setOperationModal('Copy')} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: 'var(--spacing-1) var(--spacing-3)', border: 'none', background: 'var(--color-primary-50)', color: 'var(--color-primary)', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-medium)' }}>
                        <Copy size={12} /> Copy
                      </button>
                      <button type="button" onClick={() => setOperationModal('Move')} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: 'var(--spacing-1) var(--spacing-3)', border: 'none', background: 'var(--color-warning-light)', color: 'var(--color-warning)', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-medium)' }}>
                        <ArrowRight size={12} /> Move
                      </button>
                      <button type="button" onClick={() => setOperationModal('Merge')} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: 'var(--spacing-1) var(--spacing-3)', border: 'none', background: 'var(--color-primary-50)', color: 'var(--color-primary)', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-medium)' }}>
                        <GitMerge size={12} /> Merge
                      </button>
                      <button type="button" onClick={() => setArchiveConfirm(true)} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: 'var(--spacing-1) var(--spacing-3)', border: 'none', background: 'var(--color-error-light)', color: 'var(--color-error)', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-medium)' }}>
                        <Archive size={12} /> Archive
                      </button>
                    </>
                  )}
                </div>
                <div style={{ flex: 1, overflow: 'auto', padding: 'var(--spacing-4)' }}>
                  {nodeDocuments.length === 0 && (
                    <div style={{ textAlign: 'center', padding: 'var(--spacing-10)', color: 'var(--color-gray-400)' }}>
                      <FileText size={36} style={{ margin: '0 auto var(--spacing-3)' }} />
                      <p style={{ fontSize: 'var(--font-size-sm)' }}>No documents in this folder</p>
                    </div>
                  )}
                  {nodeDocuments.map((doc) => (
                    <DocumentCard key={doc.document_id} doc={doc} selected={selectedDocIds.includes(doc.document_id)} onToggle={toggleDocSelection} />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {activeTab === 'approvals' && (
        <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-4)' }}>
            {([
              { key: 'pending' as const, label: 'Pending Approvals', count: approvalPagination.total || (pendingApprovals?.length ?? 0) },
              { key: 'access' as const, label: 'Access Requests', count: accessPagination.total || (pendingAccessReqs?.length ?? 0) },
              { key: 'case-hashtags' as const, label: 'Pending hashtag approvals', count: caseHashtagPagination.total || (pendingCaseHashtagApprovals?.length ?? 0) },
            ]).map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setApprovalSubTab(t.key)}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  borderRadius: 'var(--radius-full)',
                  border: approvalSubTab === t.key ? 'none' : '1px solid var(--color-gray-200)',
                  background: approvalSubTab === t.key ? 'var(--color-primary)' : 'white',
                  color: approvalSubTab === t.key ? 'white' : 'var(--color-gray-600)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: approvalSubTab === t.key ? 600 : 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-2)',
                  transition: 'all 0.15s',
                }}
              >
                {t.label}
                {t.count > 0 && (
                  <span style={{
                    padding: '1px 8px',
                    borderRadius: 'var(--radius-full)',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 700,
                    background: approvalSubTab === t.key ? 'rgba(255,255,255,0.25)' : 'var(--color-gray-100)',
                    color: approvalSubTab === t.key ? 'white' : 'var(--color-gray-500)',
                  }}>
                    {t.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {approvalSubTab === 'pending' && (
            <div className="card" style={{ borderRadius: 'var(--radius-lg)', flex: 1 }}>
              {!pendingApprovals || pendingApprovals.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 'var(--spacing-8)', color: 'var(--color-gray-400)' }}>
                  <CheckCircle2 size={32} style={{ margin: '0 auto var(--spacing-2)', color: 'var(--color-success)' }} />
                  <p style={{ fontSize: 'var(--font-size-sm)' }}>No pending approvals</p>
                </div>
              ) : (
                <Table
                  columns={pendingApprovalColumns}
                  rows={pendingApprovals}
                  rowKey={(item) => String(item.document_id)}
                  pagination={approvalPagination}
                  onPageChange={setApprovalPage}
                  minWidth={900}
                />
              )}
            </div>
          )}

          {approvalSubTab === 'access' && (
            <div className="card" style={{ borderRadius: 'var(--radius-lg)', flex: 1 }}>
              {!pendingAccessReqs || pendingAccessReqs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 'var(--spacing-8)', color: 'var(--color-gray-400)' }}>
                  <Lock size={32} style={{ margin: '0 auto var(--spacing-2)', color: 'var(--color-gray-300)' }} />
                  <p style={{ fontSize: 'var(--font-size-sm)' }}>No pending access requests</p>
                </div>
              ) : (
                <Table
                  columns={accessRequestColumns}
                  rows={pendingAccessReqs}
                  rowKey={(req) => String(req.request_id)}
                  pagination={accessPagination}
                  onPageChange={setAccessPage}
                  minWidth={700}
                />
              )}
            </div>
          )}

          {approvalSubTab === 'case-hashtags' && (
            <div className="card" style={{ borderRadius: 'var(--radius-lg)', flex: 1 }}>
              {!pendingCaseHashtagApprovals || pendingCaseHashtagApprovals.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 'var(--spacing-8)', color: 'var(--color-gray-400)' }}>
                  <Hash size={32} style={{ margin: '0 auto var(--spacing-2)', color: 'var(--color-success)' }} />
                  <p style={{ fontSize: 'var(--font-size-sm)' }}>No pending case hashtag approvals</p>
                </div>
              ) : (
                <Table
                  columns={caseHashtagApprovalColumns}
                  rows={pendingCaseHashtagApprovals}
                  rowKey={(item) => String(item.approval_id)}
                  pagination={caseHashtagPagination}
                  onPageChange={setCaseHashtagPage}
                  minWidth={860}
                />
              )}
            </div>
          )}
        </div>
      )}

      {archiveConfirm && (
        <>
          <div
            role="presentation"
            onClick={() => setArchiveConfirm(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 9998,
            }}
          />
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%,-50%)',
              zIndex: 9999,
              background: 'white',
              borderRadius: 'var(--radius-xl)',
              boxShadow: 'var(--shadow-xl)',
              padding: 'var(--spacing-6)',
              maxWidth: 400,
              width: '90%',
            }}
          >
            <h3
              style={{
                fontWeight: 'var(--font-weight-bold)',
                marginBottom: 'var(--spacing-2)',
              }}
            >
              Archive Documents?
            </h3>
            <p
              style={{
                color: 'var(--color-gray-600)',
                fontSize: 'var(--font-size-sm)',
                marginBottom: 'var(--spacing-5)',
              }}
            >
              You are about to archive{' '}
              <strong>
                {selectedDocIds.length} document
                {selectedDocIds.length !== 1 ? 's' : ''}
              </strong>
              . Archived documents can be found in the Archive section.
            </p>
            <div
              style={{
                display: 'flex',
                gap: 'var(--spacing-3)',
                justifyContent: 'flex-end',
              }}
            >
              <button
                type="button"
                onClick={() => setArchiveConfirm(false)}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  border: '1px solid var(--color-gray-200)',
                  borderRadius: 'var(--radius-md)',
                  background: 'white',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => archiveMutation.mutate()}
                disabled={archiveMutation.isPending}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-error)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  fontWeight: 'var(--font-weight-semibold)',
                  fontSize: 'var(--font-size-sm)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-2)',
                }}
              >
                {archiveMutation.isPending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Archive size={14} />
                )}
                {archiveMutation.isPending ? 'Archiving...' : 'Confirm Archive'}
              </button>
            </div>
          </div>
        </>
      )}

      {operationModal && (
        <DestinationPickerModal
          operationType={operationModal}
          sourceDocIds={selectedDocIds}
          onClose={() => setOperationModal(null)}
          onSuccess={() => setSelectedDocIds([])}
        />
      )}

      {viewDetailsApprovalItem && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="view-details-title"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.4)',
            padding: 'var(--spacing-4)',
          }}
          onClick={() => setViewDetailsApprovalItem(null)}
        >
          <div
            className="card"
            style={{
              maxWidth: 480,
              width: '100%',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-lg)',
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: 'var(--spacing-4)', borderBottom: '1px solid var(--color-gray-100)' }}>
              <h3 id="view-details-title" style={{ margin: 0, fontSize: 'var(--font-size-lg)', fontWeight: 600, color: 'var(--color-gray-800)' }}>
                Document details
              </h3>
            </div>
            <div style={{ padding: 'var(--spacing-4)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
              {[
                { label: 'Document', value: viewDetailsApprovalItem.original_file_name },
                { label: 'Case number', value: viewDetailsApprovalItem.cms_number },
                { label: 'RRMS ref', value: viewDetailsApprovalItem.rrms_ref_number },
                { label: 'Folder / Type', value: `${viewDetailsApprovalItem.folder_type} / ${viewDetailsApprovalItem.document_type}` },
                { label: 'Classification', value: viewDetailsApprovalItem.classification },
                { label: 'Uploaded by', value: viewDetailsApprovalItem.uploaded_by_name },
                { label: 'Date', value: format(new Date(viewDetailsApprovalItem.created_at), 'dd MMM yyyy') },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-gray-500)' }}>{label}</span>
                  <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-800)', wordBreak: 'break-word' }} title={value}>{value}</span>
                </div>
              ))}
            </div>
            <div style={{ padding: 'var(--spacing-4)', borderTop: '1px solid var(--color-gray-100)', display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setViewDetailsApprovalItem(null)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid var(--color-gray-200)',
                  background: 'white',
                  color: 'var(--color-gray-600)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  const caseId = String(viewDetailsApprovalItem.case_id);
                  setViewDetailsApprovalItem(null);
                  navigate(ROUTES.CASE_DETAIL.replace(':caseId', caseId));
                }}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  background: 'var(--color-primary)',
                  color: 'white',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-1)',
                }}
              >
                <ChevronRight size={16} />
                View case details
              </button>
            </div>
          </div>
        </div>
      )}

      {previewAccessDocId && (
        <DocumentPreviewModal
          documentId={previewAccessDocId}
          onClose={() => setPreviewAccessDocId(null)}
        />
      )}
    </div>
  );
};

export default ContentManagementPage;
